// UnifiedCSSManager.js
// Enterprise-grade CSS management system
// Eliminates fragmentation across 24+ CSS files and ensures consistent styling

import { MobileDetectionService } from './MobileDetectionService.js';

/**
 * Unified CSS management system
 * Provides consistent, optimized CSS loading and management
 * Eliminates duplicate styles and ensures proper platform separation
 */
export class UnifiedCSSManager {
    static _instance = null;
    
    /**
     * Singleton pattern for consistent CSS management
     */
    static getInstance() {
        if (!UnifiedCSSManager._instance) {
            UnifiedCSSManager._instance = new UnifiedCSSManager();
        }
        return UnifiedCSSManager._instance;
    }
    
    constructor() {
        if (UnifiedCSSManager._instance) {
            return UnifiedCSSManager._instance;
        }
        
        this.loadedStyles = new Set();
        this.sceneStyles = new Map();
        this.coreStylesLoaded = false;
        
        // Load core styles immediately
        this._loadCoreStyles();
        
        UnifiedCSSManager._instance = this;
    }
    
    /**
     * Load CSS for a specific scene
     * Automatically loads base styles and scene-specific styles
     * @param {string} sceneKey - Scene identifier
     * @param {string} sceneType - Scene type ('dashboard', 'analytics', 'hall-of-fame', etc.)
     */
    loadSceneCSS(sceneKey, sceneType) {
        console.log(`🎨 UnifiedCSSManager: Loading CSS for ${sceneKey} (${sceneType})`);
        
        // Ensure core styles are loaded
        this._ensureCoreStyles();
        
        // Load scene-specific styles
        this._loadSceneSpecificCSS(sceneType);
        
        // Track loaded styles for cleanup
        this.sceneStyles.set(sceneKey, sceneType);
        
        console.log(`✅ CSS loaded for ${sceneKey}`);
    }
    
    /**
     * Cleanup CSS for a scene
     * @param {string} sceneKey - Scene identifier
     */
    cleanupSceneCSS(sceneKey) {
        if (!this.sceneStyles.has(sceneKey)) {
            return; // No styles to cleanup
        }
        
        console.log(`🧹 UnifiedCSSManager: Cleaning up CSS for ${sceneKey}`);
        
        // Remove scene-specific styles if no other scenes are using them
        const sceneType = this.sceneStyles.get(sceneKey);
        this.sceneStyles.delete(sceneKey);
        
        // Check if any other scenes are using the same style type
        const stillInUse = Array.from(this.sceneStyles.values()).includes(sceneType);
        
        if (!stillInUse) {
            this._removeSceneSpecificCSS(sceneType);
        }
        
        console.log(`✅ CSS cleanup complete for ${sceneKey}`);
    }
    
    /**
     * Load core styles that all scenes need
     * @private
     */
    _loadCoreStyles() {
        if (this.coreStylesLoaded) return;
        
        const isMobile = MobileDetectionService.isMobile();
        
        // Core design system (check if file exists first)
        if (document.querySelector('link[href*="newDesignSystem.css"]')) {
            this._loadStylesheet('design-system', '/theme/newDesignSystem.css');
        } else {
            console.log('🎨 Design system CSS already loaded or not available');
        }
        
        // Base template styles (check if file exists first)
        if (document.querySelector('link[href*="desktop-executive-dashboard-exact.css"]')) {
            this._loadStylesheet('base-template', '/desktop-executive-dashboard-exact.css');
        } else {
            console.log('🎨 Base template CSS already loaded or not available');
        }
        
        // Shared components
        this._loadStylesheet('shared-components', '/shared-components.css');
        
        // Professional animations (already loaded by ProfessionalAnimationSystem)
        
        // Platform-specific base styles
        if (isMobile) {
            this._loadStylesheet('mobile-base', '/unified-mobile.css');
        } else {
            this._loadStylesheet('desktop-base', '/enhanced-styles.css');
        }
        
        this.coreStylesLoaded = true;
        console.log(`🎨 Core CSS loaded for ${isMobile ? 'mobile' : 'desktop'} platform`);
    }
    
    /**
     * Ensure core styles are loaded
     * @private
     */
    _ensureCoreStyles() {
        if (!this.coreStylesLoaded) {
            this._loadCoreStyles();
        }
    }
    
    /**
     * Load scene-specific CSS
     * @private
     */
    _loadSceneSpecificCSS(sceneType) {
        const styleId = `${sceneType}-scene-css`;
        
        if (this.loadedStyles.has(styleId)) {
            return; // Already loaded
        }
        
        const cssMap = {
            'analytics': '/analytics-template.css',
            'hall-of-fame': '/hall-of-fame-template.css',
            'game-center': '/game-center-template.css',
            'profile': '/profile-template.css',
            'admin-panel': '/admin-panel-template.css',
            'notifications': '/notifications-template.css'
        };
        
        const cssFile = cssMap[sceneType];
        if (cssFile) {
            this._loadStylesheet(styleId, cssFile);
        }
    }
    
    /**
     * Remove scene-specific CSS
     * @private
     */
    _removeSceneSpecificCSS(sceneType) {
        const styleId = `${sceneType}-scene-css`;
        const existingLink = document.getElementById(styleId);
        
        if (existingLink) {
            existingLink.remove();
            this.loadedStyles.delete(styleId);
            console.log(`🗑️ Removed unused CSS: ${sceneType}`);
        }
    }
    
    /**
     * Load a CSS stylesheet
     * @private
     */
    _loadStylesheet(id, href) {
        if (this.loadedStyles.has(id)) {
            return; // Already loaded
        }
        
        // Check if already exists in DOM
        if (document.getElementById(id)) {
            this.loadedStyles.add(id);
            return;
        }
        
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = href;
        
        // Add cache busting for development
        if (window.location.hostname === 'localhost') {
            link.href += `?v=${Date.now()}`;
        }
        
        link.onload = () => {
            console.log(`✅ CSS loaded: ${id}`);
        };
        
        link.onerror = () => {
            console.warn(`⚠️ CSS failed to load: ${id} from ${href} - using fallback`);
            // Remove the failed link
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
            this.loadedStyles.delete(id);
        };
        
        document.head.appendChild(link);
        this.loadedStyles.add(id);
    }
    
    /**
     * Apply theme-specific CSS variables
     * @param {Object} themeVars - CSS custom properties to apply
     */
    applyThemeVariables(themeVars) {
        const root = document.documentElement;
        
        Object.entries(themeVars).forEach(([property, value]) => {
            root.style.setProperty(`--${property}`, value);
        });
        
        console.log('🎨 Theme variables applied:', Object.keys(themeVars));
    }
    
    /**
     * Get optimal CSS settings based on device capabilities
     * @returns {Object} - CSS optimization settings
     */
    getOptimalCSSSettings() {
        const isMobile = MobileDetectionService.isMobile();
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        
        return {
            enableAnimations: !prefersReducedMotion,
            enableComplexEffects: !isMobile && !prefersReducedMotion,
            enableBackdropFilter: !isMobile, // Performance optimization
            optimizeForTouch: isMobile,
            platform: isMobile ? 'mobile' : 'desktop'
        };
    }
    
    /**
     * Inject critical CSS for immediate rendering
     * @param {string} css - Critical CSS content
     */
    injectCriticalCSS(css) {
        const criticalStyle = document.createElement('style');
        criticalStyle.id = 'critical-css';
        criticalStyle.textContent = css;
        
        // Insert at the beginning of head for highest priority
        document.head.insertBefore(criticalStyle, document.head.firstChild);
        
        console.log('🚀 Critical CSS injected for immediate rendering');
    }
    
    /**
     * Get list of loaded stylesheets
     * @returns {Array<string>} - Array of loaded stylesheet IDs
     */
    getLoadedStyles() {
        return Array.from(this.loadedStyles);
    }
    
    /**
     * Reset all CSS management (for debugging)
     */
    resetCSSManagement() {
        console.log('🔄 UnifiedCSSManager: Resetting CSS management');
        this.loadedStyles.clear();
        this.sceneStyles.clear();
        this.coreStylesLoaded = false;
    }
}

// Export singleton instance
export const unifiedCSSManager = UnifiedCSSManager.getInstance();
export default unifiedCSSManager;
